<template>
  <BaseModule
    class="container card-list"
    data-module="card-list"
    v-bind="$props"
    :style="{
      '--card-content-size-desk': `${cardTextSizeDesk}px`,
      '--card-content-size-mobile': `${cardTextSizeMobile}px`,
      '--card-text-color': cardTextColor,
      '--card-width': cardWidth + 'px',
      '--card-height': cardHeightByRatio + '%',
    }"
  >
    <div class="content">
      <header
        v-if="title || text"
        class="header base-title-fontsize"
      >
        <component
          :is="titleTag"
          v-if="title"
          class="tit tit-g"
          :style="{ fontWeight: titleWeight }"
        >
          {{ title }}
        </component>
        <div
          v-if="text"
          class="txt"
          v-html="text"
        ></div>
      </header>

      <!-- Slider main container -->
      <div
        ref="swiper"
        class="swiper"
      >
        <!-- Additional required wrapper -->
        <div
          class="swiper-wrapper"
          :class="{
            disabled: !isMobile,
          }"
        >
          <div
            v-for="(card, idx) in cards"
            :key="card.id || idx"
            class="swiper-slide aspect-ratio-parent"
          >
            <div class="aspect-ratio-child">
              <LImage
                v-if="card.image || card.imageMobile"
                class="absolute-full"
                :src="
                  isMobile && card.differentMobileBackgroundImage
                    ? card.imageMobile
                    : card.image
                "
                :alt="
                  isMobile &&
                  card.differentMobileBackgroundImage &&
                  card.altTextMobile
                    ? card.altTextMobile
                    : card.altText
                "
              />

              <article
                v-if="card.title || card.text"
                :class="{ dark: darkenCard }"
              >
                <h3
                  v-if="card.title"
                  class="tit"
                  :style="{ fontWeight: card.titleWeight }"
                >
                  {{ card.title }}
                </h3>

                <div
                  v-if="card.text"
                  class="txt"
                >
                  <span v-html="card.text"></span>
                </div>
              </article>

              <EditorLink
                v-if="showButton(undefined, card.linkUrl)"
                :model="card.linkUrl"
                class="stretched-link"
              >
              </EditorLink>
            </div>
          </div>
        </div>
        <div
          v-show="isMobile && hasMultipleCards"
          class="swiper-button-wrapper"
        >
          <div :class="`swiper-button-prev btn-prev-${instanceId}`"></div>
          <div :class="`swiper-button-next btn-next-${instanceId}`"></div>
        </div>
      </div>
    </div>
  </BaseModule>
</template>

<script>
import Swiper from 'swiper';
import { Navigation } from 'swiper/modules';
import debounce from 'lodash/debounce';

import weightOptions from '@/helpers/weightOptions.json';
import showButton from '@/mixins/showButton';
import base from '@/mixins/base';
import EditorLink from '@/modules/EditorLink.vue';
import IconArrow from '@/components/icons/IconArrow.vue';
import BaseModule from './generic/BaseModule.vue';

export default {
  name: 'CardList',
  components: {
    BaseModule,
    EditorLink,
    IconArrow,
  },
  mixins: [showButton, base],
  inheritAttrs: false,
  props: {
    ...BaseModule.props,
    text: {
      type: String,
      default: '',
    },
    buttonText: {
      type: String,
      default: '',
    },
    cards: {
      type: Array,
      default() {
        return [];
      },
    },
    cardWidth: {
      type: Number,
      default: 280,
    },
    cardHeight: {
      type: Number,
      default: 450,
    },
    cardWidthMobile: {
      type: Number,
      default: 280,
    },
    cardHeightMobile: {
      type: Number,
      default: 450,
    },
    darkenCard: {
      type: Boolean,
      default: true,
    },
    backgroundColor: {
      type: String,
      default: '#ffffff',
    },
    cardTextColor: {
      type: String,
      default: '#ffffff',
    },
    cardTextSizeDesk: {
      type: Number,
      default: 18,
    },
    cardTextSizeMobile: {
      type: Number,
      default: 16,
    },
  },
  data() {
    return {
      mobileMaxWidth: 600,
      innerWidth: null,
      swiper: null,
      isMobile: false,
    };
  },
  computed: {
    hasMultipleCards() {
      return this.cards.length > 1;
    },
    cardHeightByRatio() {
      // altura do card é calculada através de padding-top, assim as proporções do card são respeitadas quando a largura não cabe na janela
      if (this.isMobile) {
        return Number(
          ((100 / this.cardWidthMobile) * this.cardHeightMobile).toFixed(2),
        );
      }
      return Number(((100 / this.cardWidth) * this.cardHeight).toFixed(2));
    },
  },
  watch: {
    isMobile() {
      this.considerMobile();
    },
  },
  mounted() {
    this.onResize();
    this.considerMobile();
    window.addEventListener('resize', debounce(this.onResize, 150));
  },
  methods: {
    onResize() {
      const isMobile = window.innerWidth < this.mobileMaxWidth;
      this.innerWidth = window.innerWidth;

      // If "device" changed...
      if (isMobile !== this.isMobile) {
        this.isMobile = isMobile;
      }
    },
    swiperInit() {
      this.$nextTick(() => {
        this.swiper = new Swiper(this.$refs.swiper, {
          modules: [Navigation],
          observer: true,
          spaceBetween: 20,
          slidesPerView: 1.1,
          allowTouchMove: false,
          navigation: {
            nextEl: `.btn-next-${this.instanceId}`,
            prevEl: `.btn-prev-${this.instanceId}`,
          },
        });
      });
    },
    swiperDestroy() {
      this.swiper.destroy();
      this.swiper = null;
    },
    considerMobile() {
      if (this.isMobile && this.hasMultipleCards) {
        this.swiperInit();
      } else if (this.swiper) {
        this.swiperDestroy();
      }
    },
  },
  metadata: {
    extends: BaseModule.metadata,
    tabs: [
      {
        name: 'content',
        groups: [
          {
            name: 'text',
            fields: [
              {
                name: 'text',
                type: 'Html',
                label: 'Texto de apoio',
                wrapWithParagraph: false,
              },
            ],
            toRemove: ['subtitle'],
          },
          {
            name: 'cards',
            label: 'Itens',
            fields: [
              {
                name: 'cards',
                type: 'List',
                itemLabel: 'Item {index}', // any {index} will be replaced by item's index (1-based)
                addItemLabel: 'Adicionar item',
                fields: [
                  {
                    name: 'image',
                    type: 'ImageFile',
                    maxWidth: 960,
                    label: 'Imagem',
                  },
                  {
                    name: 'altText',
                    type: 'ShortText',
                    label: 'Texto alternativo',
                  },
                  {
                    name: 'differentMobileBackgroundImage',
                    type: 'Checkbox',
                    label: 'Usar uma imagem diferente para mobile',
                  },
                  {
                    name: 'imageMobile',
                    type: 'ImageFile',
                    maxWidth: 960,
                    label: 'Imagem mobile',
                    isEnabled() {
                      return this.differentMobileBackgroundImage;
                    },
                  },
                  {
                    name: 'altTextMobile',
                    type: 'ShortText',
                    label: 'Texto alternativo mobile',
                    isEnabled() {
                      return this.differentMobileBackgroundImage;
                    },
                  },
                  {
                    name: 'title',
                    type: 'ShortText',
                    label: 'Título',
                  },
                  {
                    name: 'titleWeight',
                    type: 'VueSelect',
                    label: 'Peso do título',
                    default: '300',
                    options: weightOptions,
                    isEnabled({ title }) {
                      return title;
                    },
                  },
                  {
                    name: 'text',
                    type: 'Html',
                    label: 'Texto de apoio',
                    wrapWithParagraph: false,
                  },
                  {
                    name: 'linkUrl',
                    type: 'Url',
                  },
                ],
              },
            ],
          },
          {
            name: 'config',
            label: 'Configurações',
            fields: [
              {
                name: 'cardWidth',
                label: 'Resolução de largura dos itens',
                type: 'Range',
                min: 240,
                max: 1000,
                step: 1,
                format: {
                  suffix: 'px',
                },
              },
              {
                name: 'cardHeight',
                label: 'Resolução de altura dos itens',
                type: 'Range',
                min: 240,
                max: 1000,
                step: 1,
                format: {
                  suffix: 'px',
                },
              },
              {
                name: 'cardWidthMobile',
                label: 'Resolução de largura dos itens em mobile',
                type: 'Range',
                min: 240,
                max: 1000,
                step: 1,
                format: {
                  suffix: 'px',
                },
              },
              {
                name: 'cardHeightMobile',
                label: 'Resolução de altura dos itens em mobile',
                type: 'Range',
                min: 240,
                max: 1000,
                step: 1,
                format: {
                  suffix: 'px',
                },
              },
            ],
          },
        ],
      },
      {
        name: 'appearance',
        groups: [
          {
            name: 'colors',
            fields: [
              {
                name: 'cardTextColor',
                type: 'ColorPicker',
                label: 'Cor do texto dos itens',
              },
              {
                name: 'darkenCard',
                type: 'Checkbox',
                label: 'Escurecer fundo do texto',
              },
            ],
            toRemove: ['buttonColor', 'buttonTextColor'],
          },
          {
            name: 'fonts',
            fields: [
              {
                name: 'cardTextSizeDesk',
                type: 'Range',
                label: 'Texto dos itens em desktop',
                min: 8,
                max: 100,
                step: 1,
                format: {
                  suffix: 'px',
                },
              },
              {
                name: 'cardTextSizeMobile',
                type: 'Range',
                label: 'Texto dos itens em mobile',
                min: 8,
                max: 100,
                step: 1,
                format: {
                  suffix: 'px',
                },
              },
            ],
          },
        ],
      },
    ],
  },
};
</script>

<style scoped lang="scss">
.card-list {
  padding-top: var(--padding-top);
  padding-bottom: var(--padding-bottom);

  &.container {
    max-width: 100%;
  }

  .content {
    max-width: 100%;

    .header {
      text-align: center;
      max-width: 980px;
      margin: 0 auto 32px;

      .txt {
        line-height: 1.7em;

        &::v-deep {
          strong {
            font-weight: $lb-bold !important;
          }

          a,
          a:visited {
            color: inherit;
            text-decoration: underline;
          }
        }
      }
    }

    .swiper {
      .swiper-wrapper {
        &.disabled {
          display: flex;
          flex-wrap: wrap;
          justify-content: center;
          gap: 30px;

          .swiper-slide {
            margin-right: 0 !important;

            &.aspect-ratio-parent {
              // resolução fixa de largura deve funcionar apenas quando swiper estiver desativado (.disabled)
              width: var(--card-width);
            }
          }
        }
        .swiper-slide {
          display: flex;
          flex-direction: column;
          background: rgba($lb-light-gray, 0.5);
          border-radius: 20px;
          overflow: hidden;
          padding: 0;

          &.aspect-ratio-parent {
            // com swiper ativado largura é sempre 100%
            width: 100%;
            max-width: 100%;

            .aspect-ratio-child {
              width: 100%;
              padding-top: var(--card-height);
              position: relative;
            }
          }

          article {
            width: 100%;
            padding: 60px 30px 30px;
            text-align: left;
            display: flex;
            flex-direction: column;
            flex-grow: 1;
            font-size: var(--card-content-size-desk, 18px);
            position: absolute;
            bottom: 0;

            &.dark {
              background: linear-gradient(rgba(0, 0, 0, 0), rgba(0, 0, 0, 1));
            }

            .tit {
              color: var(--card-text-color, $lb-white);
              font-weight: $lb-light;
              font-size: 1em;
              line-height: 1.4;
            }

            .txt {
              font-size: inherit;
              margin-top: 14px;

              > span {
                color: var(--card-text-color, $lb-white);
                font-size: 0.8em;
                line-height: 1.4;
                opacity: 0.7;
                font-weight: $lb-medium;
              }
            }

            @include media-breakpoint-down(sm) {
              font-size: var(--card-content-size-mobile, 16px);
            }
          }
        }
      }
      .swiper-button-wrapper {
        display: flex;
        justify-content: space-between;
        width: 160px;
        margin: 0 auto;

        @include media-breakpoint-down(lg) {
          height: 100px;
        }

        .swiper-button-prev,
        .swiper-button-next {
          position: relative;
          width: 48px;
          height: 48px;
          background: rgba(0, 0, 0, 0.2);
          border-radius: 100px;

          &::after {
            background: var(--bg-color, $lb-white);
            box-shadow: 0px 10px 16px rgba(0, 0, 0, 0.2);
            color: var(--text-color, $gray-900);
            border-radius: 100px;
            width: 48px;
            height: 48px;
            font-size: 18px;
            display: flex;
            justify-content: center;
            align-items: center;
          }
        }
      }
    }
  }

  @include media-breakpoint-down(sm) {
    padding-top: var(--padding-top-mobile);
    padding-bottom: var(--padding-bottom-mobile);
  }
}
</style>
